import React from 'react';
import propTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Megaphone, TriangleAlert, ShieldOff } from 'lucide-react';
import { getCompany } from 'helpers/getCompanyVariables';
import LoadingContent from 'components/LoadingContent/LoadingContent';
import CarrierLucideIcon from 'components/CarrierLucideIcon/CarrierLucideIcon';
import {
  getCarrierDisplayName,
  isAgentAppointedWithCarrier
} from 'helpers/appointments/appointments';
import {
  OEP_UPDATE_ICON_COLOR,
  OEP_UPDATE_ICON_DESCRIPTION
} from 'helpers/oepUpdates';
import { getSuncareCarrierCardStatus } from 'helpers/suncare/suncare';
import { cn } from 'lib/utils';

const PENDING_CARD_BG = '#ffe9d4';

// Match CarrierCardNoStates.module.scss: h2 font-size calc($font-size-subtitle - 3px)
const TITLE_FONT_SIZE = 'calc(1.33rem - 3px)';
const CARRIER_ICON_SIZE = 18;

const COLORS = {
  primaryEmphasisLight: 'var(--color-primary-emphasis-light, #39aaff)',
  greyLightest: 'var(--color-grey-lightest, #f6f6f6)',
  secondaryLight: 'var(--color-secondary-light, #e2f7b4)',
  blue: 'var(--color-blue, #39aaff)',
  grey: 'var(--color-grey, #656464)',
  semanticDanger: 'var(--color-semantic-danger, #f22344)'
};

function getCardStyles(status, isActive) {
  const base = {
    font: 'inherit',
    transition: 'filter 100ms'
  };

  if (isActive) {
    return {
      ...base,
      backgroundColor: COLORS.primaryEmphasisLight
    };
  }

  let backgroundColor = COLORS.greyLightest;
  if (status === 'Confirmed' || status === 'Active') {
    backgroundColor = COLORS.secondaryLight;
  } else if (status === 'Action Needed' || status === 'Pending Agent Action') {
    backgroundColor = '#fce8eb';
  } else if (status === 'Pending' || status === 'In progress') {
    backgroundColor = PENDING_CARD_BG;
  }
  return {
    ...base,
    backgroundColor
  };
}

const company = getCompany();

export default function CarrierCardNoStates({
  active,
  setActive,
  name,
  carrierIcon,
  carrierIconColor,
  carrierActionableAppts = [],
  agencyDisabled = false,
  showOepIcon = false
}) {
  const user = useSelector(state => state.user.zohoData);
  const {
    isSendingRequestAppointments,
    requestedCarrierName,
    requestedAppointmentsByCarrier
  } = useSelector(state => state.appointments);

  const carrierReqAppts = requestedAppointmentsByCarrier[name] ?? [];
  const isAgentAppointed = isAgentAppointedWithCarrier(name, user);
  const hasPendingAppts = carrierReqAppts.length > 0;
  const hasActionableAppts = carrierActionableAppts.length > 0;

  let status = 'Not Requested';
  if (!agencyDisabled) {
    const suncareStatus = getSuncareCarrierCardStatus(user, name);

    if (suncareStatus) {
      status = suncareStatus;
    } else if (isAgentAppointed) {
      status = company === 'oneill' ? 'Confirmed' : 'Active';
    } else if (hasActionableAppts) {
      status = 'Action Needed';
    } else if (hasPendingAppts) {
      status = 'Pending';
    }
  }

  const isActive = active === name;
  const cardStyle = getCardStyles(status, isActive);

  const getSubtitleColor = () => {
    if (isActive) return '#fff';
    if (status === 'Action Needed' || status === 'Pending Agent Action') {
      return COLORS.semanticDanger;
    }
    return COLORS.grey;
  };

  const showStatusLabel =
    (status === 'Action Needed' || status === 'Pending Agent Action') &&
    !agencyDisabled;

  const subtitleColor = getSubtitleColor();

  return (
    <button
      type='button'
      className={cn(
        'flex h-[120px] w-full min-w-0 cursor-pointer flex-col rounded-[5px] border-0 p-0 text-left hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset'
      )}
      style={cardStyle}
      onClick={() => setActive(name)}
    >
      {isSendingRequestAppointments && requestedCarrierName === name ? (
        <LoadingContent />
      ) : (
        <>
          <h2
            className='flex h-[60px] min-w-0 shrink-0 items-center justify-center gap-2 overflow-hidden px-1 pb-2 pt-4 font-primary font-semibold leading-none m-0'
            style={{
              fontSize: TITLE_FONT_SIZE,
              color: isActive ? '#fff' : COLORS.grey
            }}
          >
            <div className='flex shrink-0 items-center gap-1.5'>
              {agencyDisabled && (
                <ShieldOff
                  size={CARRIER_ICON_SIZE}
                  className='shrink-0 text-semantic-danger'
                  aria-hidden
                />
              )}
              <CarrierLucideIcon
                name={carrierIcon}
                color={carrierIconColor}
                size={CARRIER_ICON_SIZE}
                whiteIcon={isActive || status !== 'Not Requested'}
              />
              {showOepIcon && (
                <span
                  className='flex shrink-0 items-center'
                  title={OEP_UPDATE_ICON_DESCRIPTION}
                >
                  <Megaphone
                    size={CARRIER_ICON_SIZE}
                    style={{
                      color:
                        isActive || status !== 'Not Requested'
                          ? '#fff'
                          : OEP_UPDATE_ICON_COLOR
                    }}
                    aria-label={OEP_UPDATE_ICON_DESCRIPTION}
                  />
                </span>
              )}
            </div>
            <span className='min-w-0 truncate text-center'>
              {getCarrierDisplayName(name)}
            </span>
          </h2>
          <div
            className={cn(
              'flex flex-1 items-center justify-between px-2.5 py-2',
              showStatusLabel && 'flex flex-col justify-center gap-1'
            )}
          >
            {showStatusLabel && (
              <>
                {status === 'Action Needed' && (
                  <TriangleAlert
                    size={24}
                    className='shrink-0'
                    style={{
                      color: subtitleColor
                    }}
                  />
                )}
                <span
                  className='flex w-full items-center justify-center'
                  style={{
                    fontSize: 'var(--font-size-body)',
                    color: subtitleColor
                  }}
                >
                  {status}
                </span>
              </>
            )}
          </div>
        </>
      )}
    </button>
  );
}

CarrierCardNoStates.propTypes = {
  name: propTypes.string,
  carrierIcon: propTypes.string,
  carrierIconColor: propTypes.string,
  active: propTypes.string,
  setActive: propTypes.func,
  carrierActionableAppts: propTypes.array,
  agencyDisabled: propTypes.bool,
  showOepIcon: propTypes.bool
};
